import { TRACE } from '../../core/debug/debugMacros';

import { assertNotNull, AsyncResult, isFiniteNumber, Maybe } from '../../core';
import type { PlayerAdapterState } from './playerAdapterState';
import { HTMLMediaEventType, HTMLMediaNetworkState, HTMLMediaReadyState } from './htmlMediaSchema';
import { HlsManifestStatus, PlayerAdapterMessageType, SeekParams, SetAutoplayParams, SetControlsParams, SetPlaybackRateParams, SetVolumeParams } from './playerAdapterSchema';
import { setCookies } from '../../core';
import { initHlsPlayer, destroyHls } from './playerHlsState';
import { JobModel } from '../../entities';
import Hls from 'hls.js';

export const VIDEO_DOM_EVENTS: HTMLMediaEventType[] = [
  'abort',
  'canplay',
  'canplaythrough',
  'complete',
  'durationchange',
  'emptied',
  'ended',
  'error',
  'loadeddata',
  'loadedmetadata',
  'loadstart',
  'pause',
  'play',
  'playing',
  'progress',
  'ratechange',
  'seeked',
  'seeking',
  'stalled',
  'suspend',
  'timeupdate',
  'volumechange',
  'waiting',
];

/** The events for which the adapter will listen. */
const VIDEO_DOM_LISTENER_EVENTS = VIDEO_DOM_EVENTS;

export function syncState(adapter: PlayerAdapterState): void {
  const { videoElement: domVideo } = adapter;
  if (!domVideo)
    return resetState(adapter);

  const time = domVideo.currentTime;
  const buffered = domVideo.buffered;

  adapter.error = domVideo.error ?? null;
  adapter.readyState = domVideo.readyState;
  adapter.networkState = domVideo.networkState;

  adapter.playedTimeRanges = domVideo.played ?? null;
  adapter.bufferedTimeRanges = buffered ?? null;
  adapter.seekableTimeRanges = domVideo.seekable ?? null;

  adapter.duration = domVideo.duration || 0;
  adapter.clientWidth = domVideo.clientWidth || 0;
  adapter.clientHeight = domVideo.clientHeight || 0;
  adapter.videoWidth = domVideo.videoWidth || 0;
  adapter.videoHeight = domVideo.videoHeight || 0;

  adapter.autoplay = domVideo.autoplay ?? false;
  adapter.isSeeking = domVideo.seeking ?? false;
  adapter.isPaused = domVideo.paused ?? false;
  adapter.isEnded = domVideo.ended ?? false;

  adapter.currentTime = time;

  adapter.isMuted = domVideo.muted;
  adapter.volume = domVideo.volume;
  adapter.playbackRate = domVideo.playbackRate;
}

export function resetState(adapter: PlayerAdapterState): void {
  adapter.error = null;
  adapter.readyState = HTMLMediaReadyState.HAVE_NOTHING;
  adapter.networkState = HTMLMediaNetworkState.NETWORK_EMPTY;

  adapter.playedTimeRanges = null;
  adapter.bufferedTimeRanges = null;
  adapter.seekableTimeRanges = null;

  adapter.duration = 0;
  adapter.videoWidth = 0;
  adapter.videoHeight = 0;

  adapter.isSeeking = false;
  adapter.isPaused = false;
  adapter.isEnded = false;

  adapter.currentTime = 0;

  adapter.isMuted = false;
  adapter.volume = 0;

  adapter.isInitialized = false;
  adapter.hlsManifestStatus = HlsManifestStatus.None;

  adapter.fragments.clear();
  adapter.initFragment = null;
  adapter.currentFragment = null;
  adapter.isLiveStream = false;
  adapter.initPTS = null;

  adapter.playerAudioTracks.reset();
}

export function resetInitialState(adapter: PlayerAdapterState): void {
  TRACE(adapter, `Resetting initial state`);

  adapter.initialCurrentTime = null;
  adapter.initialVolume = null;
  adapter.initialPlaybackRate = null;
  adapter.initialControls = null;
  adapter.initialAutoplay = null;
  adapter.initialIsPlaying = null;
}

export function applyInitialState(adapter: PlayerAdapterState): void {
  TRACE(adapter, `Applying initial state`);

  const video = adapter.videoElement;
  if (!video) {
    TRACE(adapter, `Applying initial state suppressed because the HTMLVideoElement is not available`);
    return;
  }

  if (adapter.initialCurrentTime !== null) {
    TRACE(adapter, `Setting 'currentTime' on HTMLVideoElement because 'initialCurrentTime' is ${adapter.initialCurrentTime}`);
    video.currentTime = adapter.initialCurrentTime;
  }

  if (adapter.initialVolume !== null)
    video.volume = adapter.initialVolume;

  if (adapter.initialPlaybackRate !== null)
    video.playbackRate = adapter.initialPlaybackRate;

  if (adapter.initialAutoplay !== null) {
    TRACE(adapter, `Setting 'autoplay' on HTMLVideoElement because 'initialAutoplay' is ${adapter.initialAutoplay}`);
    video.autoplay = adapter.initialAutoplay;
  }

  switch (adapter.initialIsPlaying) {
    case true:
      TRACE(adapter, `Calling 'play' on HTMLVideoElement because 'initialIsPlaying' is true`);
      video.play();
      break;
    case false:
      TRACE(adapter, `Calling 'pause' on HTMLVideoElement because 'initialIsPlaying' is false`);
      video.pause();
      break;
  }

  if (adapter.media?.subtitle) {
    handleExternalCaptions(adapter, video);
  }

  syncState(adapter);
}

export function handleExternalCaptions(adapter: PlayerAdapterState, video: HTMLVideoElement): void {
  const track: HTMLTrackElement & { isCaption?: boolean } = document.createElement('track');
  track.id = 'transcript';
  track.isCaption = true;
  track.kind = 'metadata';
  track.label = 'English';
  track.srclang = 'en';
  track.default = true;

  if (adapter.media?.subtitle)
    track.src = adapter.media.subtitle;

  video.appendChild(track);
}

export function handleDOMEvent(adapter: PlayerAdapterState, evt: Event): void {
  if (evt.type !== 'progress' && evt.type !== 'timeupdate')
    TRACE(adapter, 'HTMLVideoElement event', evt.type, evt);

  switch (evt.type) {
    case 'loadedmetadata':
      applyInitialState(adapter);
      resetInitialState(adapter);
      break;
    case 'error':
      // hls.js renews its own source (reauthorize); a native source fails once its signature expires.
      if (!adapter.hls && adapter.jobId)
        void renewNativeSource(adapter);
  }

  syncState(adapter);
  adapter.emit(evt.type);
}

const lastNativeRenewal = new WeakMap<PlayerAdapterState, number>();

/**
 * Agency media is signed for minutes. When a native (non-hls.js) source fails, fetch freshly signed
 * URLs and resume where playback stopped. At most once per 30 s so a truly broken source can't loop.
 */
export async function renewNativeSource(adapter: PlayerAdapterState): Promise<void> {
  const video = adapter.videoElement;
  const now = Date.now();
  if (!video || now - (lastNativeRenewal.get(adapter) ?? 0) < 30_000)
    return;
  lastNativeRenewal.set(adapter, now);

  const position = video.currentTime;
  const resume = !video.paused;
  const [jobRes, err] = await adapter.store.api.getJob({ id: adapter.jobId });
  if (err || !jobRes?.getJob)
    return;
  const { media } = new JobModel(jobRes.getJob, adapter.store);
  const src = media?.basic ?? media?.hls?.signedUrl;
  if (!src)
    return;

  video.addEventListener('loadedmetadata', () => {
    video.currentTime = position;
    if (resume)
      void video.play();
  }, { once: true });
  video.src = src;
}

export function mountVideoElement(adapter: PlayerAdapterState, video: HTMLVideoElement): void {
  TRACE(adapter, `mounted()`, video);

  assertNotNull(video);
  adapter.videoElement = video;

  VIDEO_DOM_LISTENER_EVENTS.forEach(evtName =>
    video.addEventListener(evtName, adapter.handleDOMEventBound));

  tryInitPlayer(adapter);
  adapter.emit(PlayerAdapterMessageType.Mounted);
}

export function unmountVideoElement(adapter: PlayerAdapterState): void {
  TRACE(adapter, `unmounted()`);

  const video = adapter.videoElement;
  assertNotNull(video);

  TRACE(adapter, `Calling 'pause' on HTMLVideoElement and removing it's source`);
  video.pause();
  video.removeAttribute('src');
  video.childNodes.forEach(item => video.removeChild(item));
  Object.values(video.textTracks).forEach(track => track.mode = 'disabled');

  VIDEO_DOM_LISTENER_EVENTS.forEach(evtName =>
    video.removeEventListener(evtName, adapter.handleDOMEventBound));

  if (adapter.videoElement)
    adapter.videoElement.textTracks.onaddtrack = null;
  adapter.videoElement = null;

  resetState(adapter);
  resetInitialState(adapter);

  destroyHls(adapter);

  adapter.isInitialized = false;
  adapter.isLiveStream = false;
  adapter.emit('videoDetached');

  adapter.emit(PlayerAdapterMessageType.Unmounted);
}

export function reattachDOMVideo(adapter: PlayerAdapterState): void {
  TRACE(adapter, `Rettaching HTMLVideoElement`);
  if (!adapter.videoElement) {
    TRACE(adapter, `Rettaching HTMLVideoElement suppressed because the HTMLVideoElement is not available`);
    return;
  }
  const video = adapter.videoElement;
  unmountVideoElement(adapter);
  mountVideoElement(adapter, video);
}

export function tryInitPlayer(adapter: PlayerAdapterState): void {
  TRACE(adapter, `tryInitPlayer()`);

  const { media, videoElement: video } = adapter;
  if (!media || !video || adapter.isInitialized) {
    TRACE(adapter, `'tryInitPlayer' stopped because either there's no media, no video, or the player is already initialized`);
    return;
  }

  const audioSrc = media.basic;
  const videoSrc = media.hls?.signedUrl ?? media?.liveStreamUrl;
  const isLiveStream = !!media?.liveStreamUrl && !media.hls?.signedUrl;
  const cookies = media.hls?.cookieCredentials;

  if (!videoSrc && !audioSrc) {
    TRACE(adapter, `'tryInitPlayer' stopped because there is no video or audio source available`);
    return;
  }

  if (!media.isPublic && !audioSrc && !isLiveStream) {
    if (!cookies) {
      TRACE(adapter, `'tryInitPlayer' stopped because the media is not public, there is no audio source, the stream is not live and there are no cookies specified`);
      return;
    }
    setCookies(cookies);
  }

  video.autoplay = false;

  const isHlsSupported = Hls.isSupported();
  if (isHlsSupported && !audioSrc) {
    TRACE(adapter, `Attach media to Hls.js and load the source`);
    const hls = initHlsPlayer(adapter, media, video, isLiveStream, videoSrc);
    if (hls) {
      adapter.hlsManifestStatus = HlsManifestStatus.Loading;
      adapter.hls = hls;
    }
  } else if (video.canPlayType('application/vnd.apple.mpegurl') || audioSrc) {
    TRACE(adapter, `Browser natively supports HLS so the native mode will be used`);
    video.src = audioSrc ?? videoSrc;
  }

  adapter.isInitialized = true;
  adapter.emit('ready');
  adapter.isLiveStream = isLiveStream;
  TRACE(adapter, `'tryInitPlayer' completed`);
}

export function isValidSeekTime(adapter: PlayerAdapterState, time?: Maybe<number>): boolean {
  const { videoElement: domVideo } = adapter;
  if (isFiniteNumber(time) && time >= 0) {
    const duration = domVideo?.duration;
    if (!isFiniteNumber(duration)) {
      return true;
    }
    return time <= duration;
  }
  return false;
}

export async function playMedia(adapter: PlayerAdapterState): AsyncResult<boolean> {
  const { videoElement: domVideo } = adapter;
  if (!domVideo) {
    TRACE(adapter, `Call to 'play' delayed because HTMLVideoElement is not available.`);
    adapter.initialIsPlaying = true;
    return [false];
  }

  try {
    TRACE(adapter, `Calling 'play' on HTMLVideoElement`);
    await domVideo.play();
    TRACE(adapter, `Call to 'play' on HTMLVideoElement succeeded`);
    return [true];
  } catch (err) {
    TRACE(adapter, `Call to 'play' on HTMLVideoElement failed with error`, err);
    return [null, new Error('MediaPlayError')];
  }
}

export function pauseMedia(adapter: PlayerAdapterState): void {
  const { videoElement: domVideo } = adapter;
  if (!domVideo) {
    TRACE(adapter, `Call to 'pause' delayed because HTMLVideoElement is not available.`);
    adapter.initialIsPlaying = false;
    return;
  }
  TRACE(adapter, `Calling 'pause' on HTMLVideoElement`);
  domVideo.pause();
}

export function seekMedia(adapter: PlayerAdapterState, params: SeekParams): void {
  const { videoElement: domVideo } = adapter;
  const { time } = params;
  if (!isValidSeekTime(adapter, time)) {
    console.warn(`'seek' call was rejected because 'time' param is not valid:`, time);
    return;
  }

  if (!domVideo) {
    TRACE(adapter, `Call to 'seek' delayed because HTMLVideoElement is not available`);
    adapter.initialCurrentTime = time;
    return;
  }

  try {
    TRACE(adapter, `Setting 'currentTime' on HTMLVideoElement to ${time}`);
    domVideo.currentTime = time;
  } catch (err) {
    console.warn(`Error setting 'currentTime' on HTMLMediaElement: ${(err as unknown as Error).message}`);
  }
}

export function setMediaVolume(adapter: PlayerAdapterState, params: SetVolumeParams): void {
  const { videoElement: domVideo } = adapter;
  const { volume } = params;
  if (!domVideo) {
    adapter.initialVolume = volume;
    return;
  }
  domVideo.volume = volume;
}

export function setMediaPlaybackRate(adapter: PlayerAdapterState, params: SetPlaybackRateParams): void {
  const { videoElement: domVideo } = adapter;
  const { rate } = params;
  if (!domVideo) {
    adapter.initialPlaybackRate = rate;
    return;
  }
  domVideo.playbackRate = rate;
}

export function setMediaControls(adapter: PlayerAdapterState, params: SetControlsParams): void {
  const { videoElement: domVideo } = adapter;
  const { controls } = params;
  if (!domVideo) {
    TRACE(adapter, `Call to 'setControls' delayed because HTMLVideoElement is not available`);
    adapter.initialControls = controls;
    return;
  }
  TRACE(adapter, `Setting 'controls' on HTMLVideoElement to ${controls}`);
  domVideo.controls = controls;
}

export function setMediaAutoplay(adapter: PlayerAdapterState, params: SetAutoplayParams): void {
  TRACE(adapter, `setAutoplay()`, params);

  const { videoElement: domVideo } = adapter;
  const { autoplay } = params;
  if (!domVideo) {
    TRACE(adapter, `Call to 'setAutoplay' delayed because HTMLVideoElement is not available`);
    adapter.initialAutoplay = autoplay;
    return;
  }
  TRACE(adapter, `Setting 'autoplay' on HTMLVideoElement to ${autoplay}`);
  domVideo.autoplay = autoplay;
}

export async function invokeMediaAction(adapter: PlayerAdapterState, type: string, payload: SeekParams | SetVolumeParams | SetPlaybackRateParams | SetControlsParams | SetAutoplayParams | null = null): Promise<void> {
  TRACE(adapter, 'Invoke', type, payload);

  syncState(adapter);

  switch (type) {
    case 'play':
      await playMedia(adapter);
      break;

    case 'pause':
      pauseMedia(adapter);
      break;

    case 'seek':
      if (payload && 'time' in payload)
        seekMedia(adapter, payload);
      break;

    case 'setVolume':
      if (payload && 'volume' in payload)
        setMediaVolume(adapter, payload);
      break;

    case 'setPlaybackRate':
      if (payload && 'rate' in payload)
        setMediaPlaybackRate(adapter, payload);
      break;

    case 'setControls':
      if (payload && 'controls' in payload)
        setMediaControls(adapter, payload);
      break;

    case 'setAutoplay':
      if (payload && 'autoplay' in payload)
        setMediaAutoplay(adapter, payload);
      break;
  }

  syncState(adapter);
}
